import React, { useEffect, useMemo, useState } from "react";
import { useRestaurant } from "../context/RestaurantContext";
import { restaurantService } from "../services";
import {
  getDishName,
  getDishQuantity,
  getRevenueContribution,
} from "../utils/analytics";
import { TopDish } from "../utils/types";

const BestSellingItem = () => {
  const { salesData, dashboardData } = useRestaurant();
  const [items, setItems] = useState<
    { itemName: string; revenueContribution: string; quantitySold: number }[]
  >([]);
  const [loading, setLoading] = useState(false);

  const fallbackItems = useMemo(() => {
    const total = salesData?.totalSalesAmount || 0;
    return (salesData?.items || []).map((item) => ({
      itemName: item.dishName,
      revenueContribution:
        item.revenueContribution != null
          ? `${Number(item.revenueContribution).toFixed(0)}%`
          : total
            ? `${((item.totalAmount / total) * 100).toFixed(0)}%`
            : "0%",
      quantitySold: item.quantity,
    }));
  }, [salesData]);

  useEffect(() => {
    const analyticsDishes: TopDish[] = dashboardData?.analytics?.topDishes || [];
    if (analyticsDishes.length > 0) {
      setItems(
        analyticsDishes.map((dish) => ({
          itemName: getDishName(dish),
          revenueContribution: getRevenueContribution(dish),
          quantitySold: getDishQuantity(dish),
        }))
      );
      return;
    }
    if (fallbackItems.length > 0) {
      setItems(fallbackItems);
      return;
    }

    const fetchTopDishes = async () => {
      try {
        setLoading(true);
        const response: any = await restaurantService.getAnalytics(
          dashboardData?.filter || "today",
          { topLimit: 5 }
        );
        const dishes: TopDish[] = response?.data?.topDishes || [];
        setItems(
          dishes.map((dish) => ({
            itemName: getDishName(dish),
            revenueContribution: getRevenueContribution(dish),
            quantitySold: getDishQuantity(dish),
          }))
        );
      } catch (error) {
        console.error("Failed to load best selling items:", error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTopDishes();
  }, [dashboardData, fallbackItems]);

  return (
    <div className="mt-8">
      <h5 className="text-[#050505] dark:text-gray-200 font-Urbanist text-[20px] font-semibold leading-[24px] mb-6">
        Best Selling Items
      </h5>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors">
        <div className="grid grid-cols-3 gap-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
          <div className="text-[#050505] dark:text-gray-200 font-Urbanist text-[15px] font-medium text-center  border-r dark:border-gray-600 px-[20px] py-[10px]">
            Item
          </div>
          <div className="text-[#050505] dark:text-gray-200 font-Urbanist text-[15px] font-medium text-center border-r dark:border-gray-600 px-[20px] py-[10px]">
            Revenue Contribution
          </div>
          <div className="text-[#050505] dark:text-gray-200 font-Urbanist text-[15px] font-medium  text-center px-[20px] py-[10px]">
            Quantity Sold
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8A00]"></div>
          </div>
        ) : items.length === 0 ? (
          <p className="text-center text-gray-500 py-8 text-sm">
            No best-selling items for this period
          </p>
        ) : (
          items.map((item, index) => (
            <div
              key={`${item.itemName}-${index}`}
              className="grid grid-cols-3 gap-4 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] font-normal px-[20px] py-[10px] border-r dark:border-gray-700 ">
                {item.itemName}
              </div>
              <div className="text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] font-normal text-center px-[20px] py-[10px] border-r dark:border-gray-700">
                {item.revenueContribution}
              </div>
              <div className="text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] font-normal text-center px-[20px] py-[10px]">
                {item.quantitySold}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BestSellingItem;
